import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Button, DatePicker, Grid, Input, Link, Message, Modal, Tag, Typography } from "@arco-design/web-react";
import { IconClockCircle, IconCopy, IconEmail, IconSend } from "@arco-design/web-react/icon";
import moment from "moment";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FetchApi, PostApi } from "../../Api/AuthenticatedApi";
import SimpleBreadcrumbs from '../../Components/Breadcrumbs/SimpleBreadcrumbs';
import Content from "../../Layout/Content";
import LoadingState from "../../Layout/LoadingState";
import SummaryCard, { SummaryCardGroup } from "../../Layout/SummaryCard";
import MailVariantCard from "../BroadcastViewer/MailVariantCard";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {
    broadcastStatusMeta,
    canSendBroadcast,
    computeBroadcastStats,
    pickWinningVariantId,
    valueToPercent,
} from "../broadcastStats";
import { checkDeliverability } from "../../utils/deliverability";

const { Row, Col } = Grid;
const { Text } = Typography;

// valueToPercent now lives in ../broadcastStats (shared + unit-tested);
// re-exported here for any older import sites.
export { valueToPercent };

// MUI -> Arco. makeStyles -> inline style (the only used style was the link
// cursor). Grid2 /12 -> Arco Grid /24. Typography(subtitle1) -> Text; Link ->
// Arco Link; CircularProgress -> Spin. The recharts Tooltip is unrelated to MUI
// and is kept.

export default function ViewBroadcast() {
    const { broadcastId } = useParams();

    const [loading, setLoading] = React.useState(true);

    const { getAccessTokenSilently, user } = useAuth0();
    let history = useNavigate();

    const [record, setRecord] = React.useState({});
    const [mailVariants, setMailVariants] = React.useState([]);
    const [sending, setSending] = React.useState(false);
    const [confirmVisible, setConfirmVisible] = React.useState(false);
    const [testModalVisible, setTestModalVisible] = React.useState(false);
    const [testEmail, setTestEmail] = React.useState("");
    const [testSending, setTestSending] = React.useState(false);
    const [scheduleModalVisible, setScheduleModalVisible] = React.useState(false);
    const [scheduledAt, setScheduledAt] = React.useState(null); // ISO string
    const [scheduling, setScheduling] = React.useState(false);

    // Default the test recipient to the logged-in user once known.
    React.useEffect(() => {
        if (user && user.email) {
            setTestEmail(user.email);
        }
    }, [user]);

    // let setFromRecord = function (record) {
    //     setDoubleOptIn(!!record.double_opt_in);
    //     setCanDisplayForms(record.form_variants && record.form_variants.length > 0)
    //     setCanDisplayMails(record.mail_variants && record.mail_variants.length > 0)
    // }

    useEffect(() => {
        (async () => {
            let url = "/api/broadcasts/" + broadcastId
            FetchApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRecord(data);
                    // setFromRecord(data);
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to load broadcast");
                })
        })();
    }, [broadcastId, setRecord, setLoading, history, getAccessTokenSilently])

    useEffect(() => {

        (async () => {
            let url = "/api/broadcasts/" + broadcastId + "/mailvariants/"
            FetchApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setMailVariants(data);
                    // setRecord(data);
                    // setFromRecord(data);
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to load broadcast");
                })
        })();
    }, [broadcastId, setRecord, setLoading, history, getAccessTokenSilently])

    if (loading) {
        return <LoadingState />;
    }

    const stats = computeBroadcastStats(record, mailVariants);
    const activeVariantCount = (mailVariants || []).filter((v) => v.should_run !== false).length;
    const winningVariantId = pickWinningVariantId(mailVariants);
    const statusMeta = broadcastStatusMeta(record.status);
    const sendable = canSendBroadcast(record);

    // Pre-send deliverability lint across the active mail variants (deduped).
    const deliverabilityIssues = (() => {
        const seen = new Set();
        const out = [];
        (mailVariants || [])
            .filter((v) => v.should_run !== false)
            .forEach((v) => {
                checkDeliverability({
                    subject: v.mail_subject_template,
                    html: v.mail_html_template,
                    text: v.mail_text_template,
                }).forEach((issue) => {
                    if (!seen.has(issue.message)) {
                        seen.add(issue.message);
                        out.push(issue);
                    }
                });
            });
        return out;
    })();
    const chartData = [
        { name: "Sent", value: stats.sent },
        { name: "Opens", value: stats.opened },
        { name: "Conversions", value: stats.conversions },
        { name: "Unsubscribes", value: stats.unsubscribes },
    ];

    const handleSelectBroadcast = (event) => {
        // href={`/broadcasts/${broadcastId}/edit`
        history(`/broadcasts/${record.id}/edit`);
    }

    const handleDuplicate = () => {
        PostApi(getAccessTokenSilently, "/api/broadcasts/" + broadcastId + "/duplicate",
            (data) => {
                Message.success("Broadcast duplicated as a draft");
                if (data && data.id) {
                    history(`/broadcasts/${data.id}/edit`);
                }
            },
            () => history("/login"),
            () => Message.error("Failed to duplicate broadcast"),
            {},
        );
    }

    const doSend = () => {
        setSending(true);
        PostApi(getAccessTokenSilently, "/api/broadcasts/" + broadcastId + "/send",
            (data) => {
                setSending(false);
                setConfirmVisible(false);
                setRecord(data);
                Message.success("Broadcast is sending to your contact list");
            },
            (response) => {
                setSending(false);
                history("/login");
            },
            (err) => {
                setSending(false);
                // The backend returns a specific reason (e.g. "broadcast sender
                // email is not verified") — surface it so the user knows what to fix.
                Message.error((err && err.message) || "Failed to send broadcast");
            },
            {})
    }

    const doSendTest = () => {
        if (!testEmail) {
            Message.error("Enter an email address to send the test to");
            return;
        }
        setTestSending(true);
        PostApi(getAccessTokenSilently, "/api/broadcasts/" + broadcastId + "/send-test",
            (data) => {
                setTestSending(false);
                setTestModalVisible(false);
                Message.success("Test email sent to " + testEmail);
            },
            (response) => {
                setTestSending(false);
                history("/login");
            },
            (err) => {
                setTestSending(false);
                Message.error((err && err.message) || "Failed to send test email");
            },
            { email: testEmail })
    }

    const doSchedule = () => {
        if (!scheduledAt) {
            Message.error("Pick a date and time to schedule the send");
            return;
        }
        setScheduling(true);
        PostApi(getAccessTokenSilently, "/api/broadcasts/" + broadcastId + "/schedule",
            (data) => {
                setScheduling(false);
                setScheduleModalVisible(false);
                setRecord(data);
                Message.success("Broadcast scheduled");
            },
            (response) => {
                setScheduling(false);
                history("/login");
            },
            (err) => {
                setScheduling(false);
                Message.error((err && err.message) || "Failed to schedule broadcast");
            },
            { scheduled_at: scheduledAt })
    }

    return (
        <Content>
            {/* <div
                style={{
                    height: "200px",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                    filter: "contrast(75%)",
                    backgroundImage: "url(/img/wallpaper.jpeg)",
                }}
            /> */}


            <div>
                <SimpleBreadcrumbs
                    elements={
                        [
                            { name: 'Broadcasts', path: '/broadcasts' },
                            { name: record.name }

                        ]
                    }

                />
            </div>

            <div>
                {/*

                If multiple variants, then each one has it's own numbers, and they roll up into the total

                Form:
                    . visitors: the number of times your landing page has been shown
                    . conversions: the number of people who have entered their email address
                    * conversion rate: conversions / visitors

                Email:
                    . sends: the number of emails sent (Should eventually equal form.conversions)
                    . opens: the number of times the email was opened
                    . conversions: the number of times the email confirm link was clicked
                    * open rate: opens / email.sends
                    * conversion rate: conversions / opens

                Overall Success (rates):
                    * Form Visitor Email Open Rate: email.opens / form.visitors
                    * Form Conversion Email Open Rate: email.opens / form.conversions
                    * Form Visitor Email Conversion Rate: email.conversions / form.visitors
                    * Form Conversion Email Conversion Rate: email.conversions / form.conversions

                */}

                <SummaryCard
                    title={record.name}
                    titleElement={<Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
                >
                    <Text>
                        Created: {record.created_date ? moment(record.created_date).format("MMM D, YYYY") : "—"}
                    </Text>
                    <br />
                    <Text>
                        Last Modified: {record.updated_date ? moment(record.updated_date).format("MMM D, YYYY") : "—"}
                    </Text>
                    {record.sent_at && (
                        <>
                            <br />
                            <Text>Sent: {moment(record.sent_at).format("MMM D, YYYY h:mm A")}</Text>
                        </>
                    )}

                    <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
                        <Button
                            type="primary"
                            icon={<IconSend />}
                            disabled={!sendable}
                            loading={sending}
                            onClick={() => setConfirmVisible(true)}
                        >
                            Send Broadcast
                        </Button>
                        <Button
                            icon={<IconClockCircle />}
                            disabled={!sendable}
                            loading={scheduling}
                            onClick={() => setScheduleModalVisible(true)}
                        >
                            Schedule
                        </Button>
                        <Button
                            icon={<IconEmail />}
                            loading={testSending}
                            onClick={() => setTestModalVisible(true)}
                        >
                            Send Test
                        </Button>
                        <Button icon={<IconCopy />} onClick={handleDuplicate}>
                            Duplicate
                        </Button>
                        <Link style={{ cursor: 'pointer' }} onClick={handleSelectBroadcast}>Edit Broadcast</Link>
                    </div>
                    {record.status === "scheduled" && record.scheduled_at && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Scheduled to send {moment(record.scheduled_at).format("MMM D, YYYY h:mm A")}
                            </Text>
                        </div>
                    )}
                    {!sendable && record.status === "draft" && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Choose a verified sender and a contact list (in Edit Broadcast) before sending.
                            </Text>
                        </div>
                    )}

                    <Modal
                        title="Send this broadcast?"
                        visible={confirmVisible}
                        onOk={doSend}
                        confirmLoading={sending}
                        onCancel={() => setConfirmVisible(false)}
                        okText="Send now"
                        cancelText="Cancel"
                    >
                        <p>
                            This will email every contact on the selected list. This can’t be undone.
                        </p>
                        {deliverabilityIssues.length > 0 && (
                            <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--color-warning-light-1)", borderRadius: 4 }}>
                                <Text style={{ fontSize: 12, fontWeight: 600 }}>Before you send — a quick check:</Text>
                                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                                    {deliverabilityIssues.map((issue, i) => (
                                        <li key={i} style={{ fontSize: 12, color: issue.level === "error" ? "rgb(var(--red-6))" : "var(--color-text-2)" }}>
                                            {issue.message}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Modal>

                    <Modal
                        title="Send a test email"
                        visible={testModalVisible}
                        onOk={doSendTest}
                        confirmLoading={testSending}
                        onCancel={() => setTestModalVisible(false)}
                        okText="Send test"
                        cancelText="Cancel"
                    >
                        <p style={{ marginTop: 0 }}>
                            We’ll send one copy of this broadcast (subject prefixed with “[TEST]”)
                            so you can proof it. No contacts are emailed and no stats change.
                        </p>
                        <Input
                            value={testEmail}
                            onChange={(v) => setTestEmail(v)}
                            placeholder="you@example.com"
                            type="email"
                        />
                    </Modal>

                    <Modal
                        title="Schedule this broadcast"
                        visible={scheduleModalVisible}
                        onOk={doSchedule}
                        confirmLoading={scheduling}
                        onCancel={() => setScheduleModalVisible(false)}
                        okText="Schedule"
                        cancelText="Cancel"
                    >
                        <p style={{ marginTop: 0 }}>
                            Choose when to send to every contact on the selected list.
                        </p>
                        <DatePicker
                            showTime
                            style={{ width: "100%" }}
                            placeholder="Select date and time"
                            onChange={(dateString, date) => setScheduledAt(date ? date.toISOString() : null)}
                        />
                    </Modal>

                </SummaryCard>



                {stats.sent > 0 && (
                    <SummaryCard title={"Engagement funnel"}>
                        <BarChart
                            width={500}
                            height={300}
                            data={chartData}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="value" name="Count" fill="#8884d8" />
                        </BarChart>
                    </SummaryCard>
                )}







                {/* {
                    (mailVariants && mailVariants.length > 0) || (
                        <div>No Emails for this broadcast! Please <Link color={"primary"} href={`/broadcasts/${broadcastId}/email/new`}>Add
                            Email</Link> and create one</div>
                    )
                } */}

                {
                    (mailVariants && mailVariants.length > 0) && (
                        <SummaryCard title={"Email Performance"}>
                            <SummaryCardGroup>
                                <SummaryCard title={"Sent"} value={stats.sent.toLocaleString()} />
                                <SummaryCard title={"Opens"} value={stats.opened.toLocaleString()} />
                                <SummaryCard title={"Open rate"} value={valueToPercent(stats.openRate)} />
                                <SummaryCard title={"Conversions"} value={stats.conversions.toLocaleString()} />
                                <SummaryCard title={"Conversion rate"} value={valueToPercent(stats.conversionRate)} />
                                <SummaryCard title={"Unsubscribes"} value={stats.unsubscribes.toLocaleString()} />
                                <SummaryCard title={"Bounces"} value={stats.bounces.toLocaleString()} />
                                <SummaryCard title={"Complaints"} value={stats.complaints.toLocaleString()} />
                            </SummaryCardGroup>







                            {/* {
                                mailVariants.length === 1 && (
                                    <>
                                        You only have 1 email variant, click &nbsp;
                                        <Link color={"primary"} href={`/broadcasts/${broadcastId}/edit`}>Edit Broadcast</Link>
                                        &nbsp; to create more for A/B testing
                                    </>
                                )
                            } */}




                            {/* {
                                mailVariants.length > 1 && (
                                    <Link color={"primary"} href={"#"}>Compare the {record.form_variants.length} Split Test
                                        Variants</Link>
                                )
                            } */}


                        </SummaryCard>
                    )
                }

                <SummaryCard title={"Email Variations"}>
                    {activeVariantCount > 1 && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            content={`A/B test — recipients are split evenly across your ${activeVariantCount} active variants. Compare how each one performs on the cards below.`}
                        />
                    )}
                    {
                        mailVariants.length === 0 ? (
                            <div>
                                No Emails for this broadcast! Please{' '}
                                <Link style={{ cursor: 'pointer' }} onClick={() => { history(`/broadcasts/${broadcastId}/email/new`) }}>
                                    Add Email
                                </Link>{' '}
                                and create one
                            </div>
                        ) : (
                            <Row gutter={16} justify="start">
                                {
                                    mailVariants && mailVariants.map((item, index) => {
                                        return (
                                            <Col key={item.id} flex="none">
                                                <MailVariantCard item={item} index={index} editable={false} broadcastId={broadcastId} isWinner={item.id === winningVariantId} />
                                            </Col>
                                        )
                                    })
                                }

                            </Row>
                        )
                    }
                </SummaryCard>



            </div>

        </Content>
    );
}
